import React, { useEffect } from 'react';
import { getStore } from '../../../stores/StoreContext';
import { LiveKitRoom } from '@livekit/components-react';
import { SessionLayout } from './Layout';
import RecordingMonitor from './RecordingMonitor';
import TrackMonitor from './TrackMonitor';
import AudioConnection from './AudioConnection';
import CONSTANTS from '../../../config/constants';

const WebrtcMedia = () => {
  const EventStore = getStore('eventStore');
  const AppStore = getStore('appStore');
  const ExamSessionStore = getStore('examSessionStore');
  const PlayerStore = getStore('playerStore');

  useEffect(() => {
    PlayerStore.setOverlay(CONSTANTS.OVERLAYS.LIVE_LOADING_COVER);
  }, []);

  /**
   * Handles the connected event
   * @param {Room} room
   *
   * Calls props.onConnected with the room if it is defined
   * and sets isRoomConnected to true in the app store
   */
  const handleConnected = async () => {
    // Register as the participant now...
    const registrationPayload = {
      installSid: ExamSessionStore.installSid,
      examSid: ExamSessionStore.examSid,
      sessionSid: ExamSessionStore.sessionSid,
      payload: {
        referenceId: AppStore.room.localParticipant.participantInfo.sid, // this the LK internal sid...
        identity: AppStore.identity,
        type: AppStore.type,
        metadata: {},
      },
    };

    const registrationResult = await AppStore.ApiClient.registerParticipant(registrationPayload);
    if (registrationResult.successful) {
      AppStore.setIsRoomConnected(true);
      PlayerStore.setCanHaveLiveVideo(true);
      PlayerStore.setActiveVideoSource(CONSTANTS.VIDEO_SOURCES.LIVE);

      const triggerPayload = {
        participantRegistrationData: registrationResult.data,
      };

      EventStore.trigger('onConnected', triggerPayload);
    } else {
      // TODO: Show some error or notification. This is a breaking issue...
    }
  };

  /**
   * Handles the disconnected event
   *
   * Sets isRoomConnected to false in the app store
   * and calls props.onDisconnected with the room if it is defined
   */
  const handleDisconnected = (DisconnectReason) => {
    AppStore.setIsRoomConnected(false);
    EventStore.trigger('onDisconnected', DisconnectReason);
  };

  /**
   * Handles the connection error event
   *
   * Sets isRoomConnected to false in the app store
   * and calls props.onConnectionError with the error if it is defined
   *
   * @param {Error} error - The error object encountered during connection
   */
  const handleConnectionError = (error) => {
    AppStore.setIsRoomConnected(false);
    EventStore.trigger('onConnectionError', error);
  };

  /**
   * Handles the media device failure event
   *
   * Sets isRoomConnected to false in the app store
   * and calls props.onMediaDeviceFailure with the MediaDeviceFailure if it is defined
   *
   * @param {MediaDeviceFailure} MediaDeviceFailure - The MediaDeviceFailure object encountered during connection
   */
  const handleMediaDeviceFailure = (MediaDeviceFailure) => {
    EventStore.trigger('onMediaDeviceFailure', MediaDeviceFailure);
  };

  /**
   * Handles the encryption error event
   *
   * Sets isRoomConnected to false in the app store
   * and calls props.onEncryptionError with the error if it is defined
   *
   * @param {Error} error - The error object encountered during encryption
   */
  const handleEncryptionError = (error) => {
    EventStore.trigger('onEncryptionError', error);
  };

  return (
    <LiveKitRoom
      room={AppStore?.room}
      token={AppStore?.mediaClient?.mediaToken}
      serverUrl={AppStore?.websocketUrl}
      connect={true}
      onConnected={handleConnected}
      onDisconnected={handleDisconnected}
      onError={handleConnectionError}
      onEncryptionError={handleEncryptionError}
      onMediaDeviceFailure={handleMediaDeviceFailure}
    >
      {/* Monitor components handle LiveKit hooks to prevent SessionLayout re-renders */}
      <RecordingMonitor />
      <TrackMonitor />
      {/* PR-3412: AudioConnection with explicit room prop */}
      <AudioConnection room={AppStore?.room} />
      <SessionLayout />
    </LiveKitRoom>
  );
};

export default WebrtcMedia;
